import React, { useState, useEffect, useMemo, lazy, Suspense } from 'react';
import { BrowserRouter as Router, Routes, Route, useNavigate } from 'react-router-dom';
import { Header, Footer, Card, Button, ScrollToTop, useToast, ToastProvider } from './components';
import ProtectedRoute from './components/ProtectedRoute';
import { productService, Product } from './services/product.service';
import { cartService } from './services/cart.service';
import { wishlistService } from './services/wishlist.service';
import { formatCurrency } from './utils/price';
import { getProductImage, handleImageError } from './utils/image';
import { authService } from './services/auth.service';
import { usePrefetch } from './utils/performance';
//Update Less
const Login = lazy(() => import('./pages/Login'));
const AdminLogin = lazy(() => import('./pages/AdminLogin'));
const Signup = lazy(() => import('./pages/Signup'));
const ForgotPassword = lazy(() => import('./pages/ForgotPassword'));
const ResetPassword = lazy(() => import('./pages/ResetPassword'));
const ProductDetails = lazy(() => import('./pages/ProductDetails'));
const AdminPage = lazy(() => import('./pages/AdminPage'));
const UserProfile = lazy(() => import('./pages/UserProfile'));
const TermsOfService = lazy(() => import('./pages/TermsOfService'));
const PrivacyPolicy = lazy(() => import('./pages/PrivacyPolicy'));
const Cart = lazy(() => import('./pages/Cart'));
const Checkout = lazy(() => import('./pages/Checkout'));
const OrderConfirmation = lazy(() => import('./pages/OrderConfirmation'));
const Wishlist = lazy(() => import('./pages/Wishlist'));
const Search = lazy(() => import('./pages/Search'));
const Orders = lazy(() => import('./pages/Orders'));
const About = lazy(() => import('./pages/About'));
const Contact = lazy(() => import('./pages/Contact'));
const Developer = lazy(() => import('./pages/Developer'));

const CATEGORY_ICONS: Record<string, string> = {
  trees: '🌳',
  tools: '🔧',
  pots: '🏺',
  accessories: '✨',
};

const DEFAULT_CATEGORIES = [
  {
    name: "Trees",
    description: "Discover our bonsai tree collection",
    icon: "🌳"
  }
];

function HomePage() {
  const navigate = useNavigate();
  const toast = useToast();
  const [currentSlide, setCurrentSlide] = useState(0);
  const [products, setProducts] = useState<Product[]>([]);
  const [popularProducts, setPopularProducts] = useState<Product[]>([]);
  const [categories, setCategories] = useState<string[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [isAuthenticated, setIsAuthenticated] = useState(false);

  // Custom styles for mobile carousel
  const carouselStyles = `
    .scrollbar-hide {
      -ms-overflow-style: none;
      scrollbar-width: none;
    }
    .scrollbar-hide::-webkit-scrollbar {
      display: none;
    }
    .snap-x {
      scroll-snap-type: x mandatory;
    }
    .snap-center {
      scroll-snap-align: center;
    }
  `;

  // Enable prefetching for better performance
  usePrefetch();

  // Check authentication status
  useEffect(() => {
    const checkAuth = () => {
      const authStatus = authService.isAuthenticated();
      setIsAuthenticated(authStatus);
    };

    checkAuth();

    // Listen for authentication changes
    const handleAuthChange = () => {
      checkAuth();
    };

    window.addEventListener('storage', handleAuthChange);
    window.addEventListener('authFailed', handleAuthChange);

    return () => {
      window.removeEventListener('storage', handleAuthChange);
      window.removeEventListener('authFailed', handleAuthChange);
    };
  }, []);

  // Fetch a lightweight slice of products for the home page
  useEffect(() => {
    const fetchProducts = async () => {
      try {
        setLoading(true);
        const [popular, featured, fetchedCategories] = await Promise.all([
          productService.getPopularProducts(),
          productService.getFeaturedPreview(12),
          productService.getCategories()
        ]);

        setPopularProducts(popular.slice(0, 8));
        setProducts(featured);
        setCategories(fetchedCategories || []);
      } catch (err) {
        setError('Failed to load products');
      } finally {
        setLoading(false);
      }
    };

    fetchProducts();
  }, []);

  const handleLearnMore = () => {
    navigate('/about');
  };

  const handleMenuClick = () => {
    alert('Mobile menu clicked!');
  };

  const handleAddToCart = async (product: Product) => {
    // Check if user is authenticated
    if (!authService.isAuthenticated()) {
      toast.warning('Login Required', 'Please login to add items to your cart');
      navigate('/login');
      return;
    }

    // Check if product is available and in stock
    if (!product.available) {
      toast.error('Out of Stock', 'This product is currently out of stock and cannot be added to cart');
      return;
    }

    if (product.stock <= 0) {
      toast.error('Out of Stock', 'This product is currently out of stock and cannot be added to cart');
      return;
    }

    try {
      await cartService.addToCart({ productId: product.id, quantity: 1 });
      // Dispatch event to update header cart count
      window.dispatchEvent(new CustomEvent('cartUpdated'));
      // Show success feedback with cool notification
      toast.cartSuccess(product.name);
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to add to cart';
      toast.error('Failed to Add to Cart', errorMessage);
    }
  };

  const handleAddToWishlist = async (product: Product) => {
    // Check if user is authenticated
    if (!authService.isAuthenticated()) {
      toast.warning('Login Required', 'Please login to add items to your wishlist');
      navigate('/login');
      return;
    }

    try {
      await wishlistService.addToWishlist(product.id);
      // Dispatch event to update header wishlist count
      window.dispatchEvent(new CustomEvent('wishlistUpdated'));
      // Show success feedback with cool notification
      toast.wishlistSuccess(product.name);
    } catch (err) {
      toast.error('Failed to Add to Wishlist', 'Please try again');
    }
  };

  const handleProductClick = (productId: number) => {
    navigate(`/product/${productId}`);
  };

  const handleCategoryClick = (categoryName: string) => {
    navigate(`/search?category=${encodeURIComponent(categoryName)}`);
  };

  const handleViewAllProducts = () => {
    navigate('/search');
  };

  const handleViewCart = () => {
    navigate('/cart');
  };

  const handleViewWishlist = () => {
    navigate('/wishlist');
  };

  const handleContactUs = () => {
    navigate('/contact');
  };

  // Auto-scroll carousel
  useEffect(() => {
    if (popularProducts.length === 0) return;

    const interval = setInterval(() => {
      setCurrentSlide((prev) => (prev + 1) % popularProducts.length);
    }, 3000); // Change slide every 3 seconds

    return () => clearInterval(interval);
  }, [popularProducts.length]);

  const displayCategories = useMemo(() => {
    if (categories.length > 0) {
      return categories.map((cat) => ({
        name: cat,
        description: `Explore our ${cat.toLowerCase()} selection`,
        icon: CATEGORY_ICONS[cat.toLowerCase()] || '🪴'
      }));
    }
    return DEFAULT_CATEGORIES;
  }, [categories]);

  // rating UI removed

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-green-50 via-emerald-50 to-teal-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-32 w-32 border-b-2 border-green-600 mx-auto mb-4"></div>
          <p className="text-green-600 text-lg">Loading amazing products...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-green-50 via-emerald-50 to-teal-50 flex items-center justify-center">
        <div className="text-center">
          <div className="text-red-600 text-6xl mb-4">⚠️</div>
          <p className="text-red-600 text-lg mb-4">{error}</p>
          <Button onClick={() => window.location.reload()} variant="primary">
            Try Again
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-green-50 via-emerald-50 to-teal-50 relative overflow-hidden">
      <style>{carouselStyles}</style>

      {/* Resource hints for better performance */}
      <link rel="preconnect" href={(import.meta.env && import.meta.env.VITE_API_URL) || ''} />
      <link rel="dns-prefetch" href={(import.meta.env && import.meta.env.VITE_API_URL) || ''} />

      {/* Background Leaf Pattern */}
      <div className="absolute inset-0 opacity-5">
        <div className="absolute top-20 left-10 text-8xl text-green-600 transform rotate-12">🍃</div>
        <div className="absolute top-40 right-20 text-6xl text-green-500 transform -rotate-12">🌿</div>
        <div className="absolute bottom-40 left-20 text-7xl text-green-600 transform rotate-45">🌱</div>
        <div className="absolute bottom-20 right-10 text-5xl text-green-500 transform -rotate-30">🍀</div>
        <div className="absolute top-1/2 left-1/4 text-6xl text-green-400 transform rotate-15">🌾</div>
        <div className="absolute top-1/3 right-1/3 text-5xl text-green-600 transform -rotate-45">🌺</div>
      </div>

      <Header onMenuClick={handleMenuClick} />

      {/* Authentication Banner for Unauthenticated Users */}
      {!isAuthenticated && (
        <div className="bg-gradient-to-r from-blue-500 to-blue-600 text-white py-3 px-4 text-center">
          <div className="max-w-7xl mx-auto flex items-center justify-center gap-2">
            <span className="text-sm">🔒 Please login to add products to your cart and wishlist</span>
            <Button
              variant="secondary"
              size="small"
              onClick={() => navigate('/login')}
              className="ml-4 bg-white text-blue-600 hover:bg-gray-100"
            >
              Login Now
            </Button>
          </div>
        </div>
      )}

      <main className="flex-1 relative z-10">
        {/* Hero Section with Product Slider */}
        <section className="relative py-20 overflow-hidden">
          {/* Hero Background with Nature Elements */}
          <div className="absolute inset-0 bg-gradient-to-br from-emerald-100/50 via-green-50 to-teal-100/50">
            {/* Floating Leaves */}
            <div className="absolute top-10 left-10 text-4xl text-green-500/20">🍃</div>
            <div className="absolute top-20 right-20 animate-pulse text-3xl text-emerald-500/20">🌿</div>
            <div className="absolute bottom-20 left-20 text-5xl text-teal-500/20">🌱</div>
            <div className="absolute bottom-10 right-10 animate-pulse text-4xl text-green-500/20" style={{ animationDelay: '2s' }}>🍀</div>
          </div>

          <div className="relative z-10 max-w-8xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="flex flex-col justify-center items-center min-h-[200px]">
              {/* Product Slider - Full Width */}
              <div className="w-full max-w-7xl">
                <div className="text-center mb-6 sm:mb-10">
                  <h3 className="text-2xl sm:text-3xl lg:text-4xl font-bold text-green-800 mb-2 sm:mb-4">🌟 Most Popular Products</h3>
                  <p className="text-lg sm:text-xl text-green-600">Discover our best-selling bonsai collection</p>
                </div>

                {/* Product Carousel */}
                <div className="relative">
                  <div className="flex space-x-4 sm:space-x-6 lg:space-x-8 overflow-x-auto scrollbar-hide snap-x snap-mandatory pb-8">
                    {popularProducts.map((product, index) => (
                      <div
                        key={product.id}
                        className={`flex-shrink-0 w-72 sm:w-80 lg:w-96 bg-white rounded-xl sm:rounded-2xl p-4 sm:p-5 lg:p-6 border border-gray-100 hover:shadow-xl transition-all duration-500 cursor-pointer transform shadow-md snap-center ${index === currentSlide ? 'scale-105 opacity-100' : 'scale-95 opacity-70'
                          }`}
                        onClick={() => handleProductClick(product.id)}
                        onKeyDown={(e) => {
                          if (e.key === 'Enter' || e.key === ' ') {
                            e.preventDefault();
                            handleProductClick(product.id);
                          }
                        }}
                        tabIndex={0}
                        role="button"
                        aria-label={`View details for ${product.name}`}
                        style={{
                          transform: `translateX(-${currentSlide * 100}%)`,
                          transition: 'transform 0.5s ease-in-out'
                        }}
                      >
                        {/* Product Image Container */}
                        <div className="relative bg-gray-50 rounded-lg sm:rounded-xl h-48 sm:h-56 mb-4 overflow-hidden group">
                          <img
                            src={getProductImage(product.imageUrl)}
                            alt={product.name}
                            loading="lazy"
                            className="w-full h-full object-contain group-hover:scale-105 transition-transform duration-500"
                            onError={handleImageError}
                          />
                          {/* Floating Badge */}
                          <div className="absolute top-2 sm:top-4 right-2 sm:right-4 bg-green-500 text-white px-2 sm:px-3 py-1 rounded-full text-xs sm:text-sm font-semibold shadow-md">
                            Popular
                          </div>
                          {!product.available && (
                            <div className="absolute inset-0 bg-black bg-opacity-50 flex items-center justify-center">
                              <span className="text-white font-medium">Out of Stock</span>
                            </div>
                          )}
                        </div>

                        {/* Product Info */}
                        <div className="space-y-3">
                          <h4 className="text-lg sm:text-xl font-bold text-gray-800 leading-tight">
                            {product.name}
                          </h4>

                          <p className="text-sm text-gray-600 line-clamp-2">
                            {product.description || 'Beautiful bonsai tree for your collection'}
                          </p>

                          {/* Price Row */}
                          <div className="flex flex-col">
                            {product.discountPrice && product.discountPrice < product.price ? (
                              <>
                                <span className="text-xl sm:text-2xl font-bold text-green-600">
                                  {formatCurrency(product.discountPrice)}
                                </span>
                                <span className="text-sm text-gray-400 line-through">
                                  {formatCurrency(product.price)}
                                </span>
                              </>
                            ) : (
                              <span className="text-xl sm:text-2xl font-bold text-green-600">
                                {formatCurrency(product.price)}
                              </span>
                            )}
                          </div>

                          {/* Action Buttons */}
                          <div className="flex gap-2 pt-2">
                            <Button
                              variant="primary"
                              size="small"
                              className="flex-1 bg-[#15803d] hover:bg-[#166534] text-white !py-2.5"
                              disabled={!product.available}
                              onClick={(e) => {
                                e.stopPropagation();
                                if (isAuthenticated) {
                                  handleAddToCart(product);
                                } else {
                                  toast.warning('Login Required', 'Please login to add items to your cart');
                                  navigate('/login');
                                }
                              }}
                            >
                              {!isAuthenticated ? 'Login to Add' : (product.available ? 'Add to Cart' : 'Out of Stock')}
                            </Button>
                            <Button
                              variant="secondary"
                              size="small"
                              className="bg-[#6ee7b7] hover:bg-[#34d399] text-[#064e3b] border-none !px-4 !py-2.5"
                              onClick={(e) => {
                                e.stopPropagation();
                                if (isAuthenticated) {
                                  handleAddToWishlist(product);
                                } else {
                                  toast.warning('Login Required', 'Please login to add items to your wishlist');
                                  navigate('/login');
                                }
                              }}
                            >
                              {isAuthenticated ? '♥' : '♡'}
                            </Button>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>

                  {/* Carousel Navigation Dots */}
                  <div className="flex justify-center mt-2 sm:mt-4 space-x-2 sm:space-x-4">
                    {popularProducts.map((_, index) => (
                      <button
                        key={index}
                        onClick={() => setCurrentSlide(index)}
                        className={`w-3 h-3 sm:w-4 sm:h-4 rounded-full transition-all duration-300 ${index === currentSlide
                          ? 'bg-green-600 scale-125 shadow-md shadow-green-600/30'
                          : 'bg-green-200 hover:bg-green-300 hover:scale-110'
                          }`}
                      />
                    ))}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Categories Section with Nature Background */}
        <section className="py-16 bg-gradient-to-br from-emerald-100 via-green-100 to-teal-100 relative">
          {/* Floating Nature Elements */}
          <div className="absolute inset-0 overflow-hidden">
            <div className="absolute top-10 left-10 text-4xl text-green-400/30">🍃</div>
            <div className="absolute top-20 right-20 text-3xl text-emerald-400/40 animate-pulse" style={{ animationDelay: '1s' }}>🌿</div>
            <div className="absolute bottom-20 left-20 text-5xl text-teal-400/30">🌱</div>
            <div className="absolute bottom-10 right-10 text-4xl text-green-400/40 animate-pulse" style={{ animationDelay: '3s' }}>🍀</div>
          </div>

          <div className="relative z-10 max-w-7xl mx-auto px-8 flex flex-col items-center justify-center">
            <div className="text-center mb-12 w-full">
              <div className="flex items-center justify-center mb-4">
                <span className="text-2xl mr-3">🌿</span>
                <h2 className="text-4xl font-bold text-green-800">Shop by Category</h2>
              </div>
              <p className="text-green-600 text-lg">Explore our complete bonsai collection</p>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 w-full">
              {displayCategories.map((category, index) => (
                <button
                  key={index}
                  onClick={() => handleCategoryClick(category.name)}
                  className="w-full py-4 px-6 bg-white text-green-800 font-semibold rounded-xl border border-green-200 hover:border-green-400 hover:bg-green-50 transition-all duration-200 shadow-sm hover:shadow-md focus:outline-none focus:ring-2 focus:ring-green-400"
                >
                  {category.name}
                </button>
              ))}
            </div>
          </div>
        </section>

        {/* Featured Products Section */}
        <section className="py-20 bg-white/50 backdrop-blur-sm">
          <div className="relative z-10 max-w-7xl mx-auto px-8 flex flex-col items-center justify-center">
            <div className="text-center mb-12 w-full">
              <div className="flex items-center justify-center mb-4">
                <span className="text-2xl mr-3">🌱</span>
                <h2 className="text-4xl font-bold text-green-800">Featured Products</h2>
              </div>
              <p className="text-green-600 text-lg">Discover our handpicked selection of premium bonsai</p>
            </div>

            {products.length > 0 ? (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8 w-full justify-items-center">
                {products.slice(0, 8).map((product) => (
                  <Card
                    key={product.id}
                    variant="elevated"
                    size="medium"
                    className="bg-white hover:shadow-xl transition-all duration-300 hover:-translate-y-2 cursor-pointer group"
                    onClick={() => handleProductClick(product.id)}
                  >
                    <div className="relative overflow-hidden rounded-lg mb-4 h-48 bg-gray-50">
                      <img
                        src={getProductImage(product.imageUrl)}
                        alt={product.name}
                        loading="lazy"
                        className="absolute inset-0 w-full h-full object-contain group-hover:scale-105 transition-transform duration-300"
                        onError={handleImageError}
                      />
                      {product.createdAt && new Date(product.createdAt).getTime() > Date.now() - 7 * 24 * 60 * 60 * 1000 && (
                        <div className="absolute top-2 left-2 bg-blue-500 text-white text-xs px-2 py-1 rounded-full">
                          New
                        </div>
                      )}
                      {!product.available && (
                        <div className="absolute inset-0 bg-black bg-opacity-50 flex items-center justify-center">
                          <span className="text-white font-medium">Out of Stock</span>
                        </div>
                      )}
                    </div>

                    <div className="p-4">
                      <h3 className="font-semibold text-gray-800 mb-2 text-lg line-clamp-2 group-hover:text-green-600 transition-colors">
                        {product.name}
                      </h3>

                      <p className="text-gray-600 text-sm mb-3 line-clamp-2">
                        {product.description || 'Beautiful bonsai tree for your collection'}
                      </p>

                      <div className="flex flex-col">
                        {product.discountPrice && product.discountPrice < product.price ? (
                          <>
                            <span className="text-2xl font-bold bg-gradient-to-r from-green-600 via-emerald-600 to-teal-600 bg-clip-text text-transparent drop-shadow-sm">
                              {formatCurrency(product.discountPrice)}
                            </span>
                            <span className="text-sm text-gray-400 line-through">
                              {formatCurrency(product.price)}
                            </span>
                          </>
                        ) : (
                          <span className="text-2xl font-bold bg-gradient-to-r from-green-600 via-emerald-600 to-teal-600 bg-clip-text text-transparent drop-shadow-sm">
                            {formatCurrency(product.price)}
                          </span>
                        )}
                      </div>
                      {/* Rating removed */}

                      <div className="flex gap-2">
                        <Button
                          variant="primary"
                          size="small"
                          className="flex-1"
                          disabled={!product.available}
                          onClick={(e) => {
                            e.stopPropagation();
                            if (isAuthenticated) {
                              handleAddToCart(product);
                            } else {
                              toast.warning('Login Required', 'Please login to add items to your cart');
                              navigate('/login');
                            }
                          }}
                        >
                          {!isAuthenticated ? 'Login to Add' : (product.available ? 'Add to Cart' : 'Out of Stock')}
                        </Button>
                        <Button
                          variant="secondary"
                          size="small"
                          onClick={(e) => {
                            e.stopPropagation();
                            if (isAuthenticated) {
                              handleAddToWishlist(product);
                            } else {
                              toast.warning('Login Required', 'Please login to add items to your wishlist');
                              navigate('/login');
                            }
                          }}
                        >
                          {isAuthenticated ? '♥' : '♡'}
                        </Button>
                      </div>
                    </div>
                  </Card>
                ))}
              </div>
            ) : (
              <div className="text-center py-12">
                <div className="text-gray-400 text-6xl mb-4">🌱</div>
                <h3 className="text-xl font-medium text-gray-600 mb-2">No products available</h3>
                <p className="text-gray-500">Check back soon for new arrivals!</p>
              </div>
            )}

            {products.length > 8 && (
              <div className="text-center mt-12 w-full flex justify-center">
                <Button variant="primary" size="large" onClick={handleViewAllProducts}>
                  View All Products →
                </Button>
              </div>
            )}
          </div>
        </section>



        {/* Call-to-Action Section */}
        <section className="py-16 bg-gradient-to-br from-green-500 to-emerald-600 text-white">
          <div className="relative z-10 max-w-7xl mx-auto px-8">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-12">
              <div className="text-center">
                <h3 className="text-3xl font-bold mb-4">Quick Links</h3>
                <ul className="space-y-4">
                  <li onClick={handleViewAllProducts} className="cursor-pointer hover:text-white/90 transition-colors">All Products</li>
                  <li onClick={handleViewCart} className="cursor-pointer hover:text-white/90 transition-colors">View Cart</li>
                  <li onClick={handleViewWishlist} className="cursor-pointer hover:text-white/90 transition-colors">Wishlist</li>
                  <li onClick={handleContactUs} className="cursor-pointer hover:text-white/90 transition-colors">Contact Us</li>
                </ul>
              </div>
              <div className="text-center">
                <h3 className="text-3xl font-bold mb-4">About Us</h3>
                <p className="text-lg mb-4">
                  We are passionate about bonsai, offering a wide range of high-quality bonsai trees, tools, and accessories.
                  Our mission is to provide exceptional bonsai experiences for enthusiasts of all levels.
                </p>
                <Button variant="secondary" size="medium" onClick={handleLearnMore} className="bg-white text-green-600 hover:bg-white/90">
                  Learn More →
                </Button>
              </div>
              {/* <div className="text-center">
                <h3 className="text-3xl font-bold mb-4">Newsletter</h3>
                <p className="text-lg mb-4">
                  Subscribe to our newsletter to receive updates about new products, promotions, and bonsai tips.
                </p>
                <div className="flex gap-2">
                  <input type="email" placeholder="Enter your email" className="p-3 rounded-lg border border-white/50 text-black" />
                  <Button variant="primary" size="medium" className="bg-white text-green-600 hover:bg-white/90">
                    Subscribe
                  </Button>
                </div>
              </div> */}
            </div>
          </div>
        </section>
      </main>

      <Footer />
    </div>
  );
}

function App() {
  return (
    <ToastProvider>
      <Router future={{ v7_startTransition: true, v7_relativeSplatPath: true }}>
        <ScrollToTop />
        <Suspense fallback={
          <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-green-50 via-emerald-50 to-teal-50">
            <div className="text-center text-green-700">Loading...</div>
          </div>
        }>
          <Routes>
            <Route path="/" element={<HomePage />} />
            <Route path="/login" element={<Login />} />
            <Route path="/admin-login" element={<AdminLogin />} />
            <Route path="/signup" element={<Signup />} />
            <Route path="/forgot-password" element={<ForgotPassword />} />
            <Route path="/reset-password" element={<ResetPassword />} />
            <Route path="/product/:id" element={<ProductDetails />} />
            <Route path="/admin" element={
              <ProtectedRoute requireAdmin={true}>
                <AdminPage />
              </ProtectedRoute>
            } />
            <Route path="/profile" element={
              <ProtectedRoute>
                <UserProfile />
              </ProtectedRoute>
            } />
            <Route path="/terms" element={<TermsOfService />} />
            <Route path="/privacy" element={<PrivacyPolicy />} />
            <Route path="/cart" element={
              <ProtectedRoute>
                <Cart />
              </ProtectedRoute>
            } />
            <Route path="/checkout" element={
              <ProtectedRoute>
                <Checkout />
              </ProtectedRoute>
            } />
            <Route path="/order-confirmation" element={
              <ProtectedRoute>
                <OrderConfirmation />
              </ProtectedRoute>
            } />
            <Route path="/wishlist" element={
              <ProtectedRoute>
                <Wishlist />
              </ProtectedRoute>
            } />
            <Route path="/search" element={<Search />} />
            <Route path="/orders" element={
              <ProtectedRoute>
                <Orders />
              </ProtectedRoute>
            } />
            <Route path="/about" element={<About />} />
            <Route path="/contact" element={<Contact />} />
            <Route path="/developer" element={<Developer />} />
          </Routes>
        </Suspense>
      </Router>
    </ToastProvider>
  );
}

export default App;
